import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

/**
 * v43.8 (Mar 2026) — Customer self-service order import UI.
 *
 * Reuses backend routes /api/customer/order-imports/* (v43.6). Backend forces
 * customer_id from JWT; frontend never sends it. Admin mapping / product
 * search / arbitrary pricing edits are NOT exposed to Customer.
 */

const STATUS_LABEL = {
  UPLOADED: "In analisi",
  REVIEW_REQUIRED: "Da verificare",
  READY: "Pronto per conferma",
  CONFIRMED: "Ordine creato",
  REJECTED: "Non importabile",
};

const STATUS_TONE = {
  UPLOADED: { bg: "#dbeafe", fg: "#1e40af" },
  REVIEW_REQUIRED: { bg: "#fef3c7", fg: "#92400e" },
  READY: { bg: "#dcfce7", fg: "#166534" },
  CONFIRMED: { bg: "#d1fae5", fg: "#065f46" },
  REJECTED: { bg: "#fee2e2", fg: "#991b1b" },
};

function StatusPill({ status }) {
  const label = STATUS_LABEL[status] || status;
  const t = STATUS_TONE[status] || { bg: "#e5e7eb", fg: "#374151" };
  return (
    <span data-testid={`import-status-${status}`}
          style={{ background: t.bg, color: t.fg, padding: "3px 10px",
                   borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
      {label}
    </span>
  );
}

const eur = (v) => v == null ? "—"
  : v.toLocaleString("it-IT", { style: "currency", currency: "EUR" });

function friendlyError(e) {
  const code = e?.data?.detail?.code;
  const msg = e?.data?.detail?.message;
  if (msg) return msg;
  if (code === "PILOT_ONLY") return "Funzione disponibile solo per i Customer pilota.";
  if (code === "DUPLICATE_IMPORT") return "Questo documento risulta già caricato.";
  if (code === "PROFILE_NOT_VERIFIED") return "Prima di inviare l'ordine devi verificare i dati della tua azienda.";
  if (typeof e?.data?.detail === "string") return e.data.detail;
  if (e?.status === 413) return "Il file supera la dimensione massima (10 MB).";
  if (e?.status === 415) return "Formato file non supportato. Accettati: PDF, XLS, XLSX, CSV.";
  if (e?.status === 403) return "Non hai i permessi per questa operazione.";
  return "Si è verificato un errore durante l'analisi del documento.";
}

/* ---------- Upload card ---------- */
function UploadCard({ onUploaded }) {
  const { apiGet } = useAuth();
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const [existingId, setExistingId] = useState(null);

  const submit = async () => {
    if (!file) return;
    setUploading(true); setErr(null); setExistingId(null);
    const fd = new FormData();
    fd.append("file", file);
    if (note) fd.append("notes", note);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer/order-imports`, {
        method: "POST", credentials: "include", body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw { status: r.status, data: j };
      onUploaded(j.id);
    } catch (e) {
      setErr(friendlyError(e));
      if (e?.data?.detail?.code === "DUPLICATE_IMPORT") {
        setExistingId(e.data.detail.existing_import_id || null);
      }
    } finally { setUploading(false); }
  };

  return (
    <div data-testid="import-upload-card"
         style={{ background: "#fff", borderRadius: 12, padding: 24,
                  boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: 24 }}>
      <h2 style={{ fontSize: 20, margin: "0 0 8px", fontWeight: 700 }}>Carica un nuovo documento</h2>
      <p style={{ color: "#666", fontSize: 14, margin: "0 0 16px" }}>
        Accettati: PDF, XLS, XLSX, CSV — max 10 MB. Il file resta accessibile
        nel tuo elenco importazioni.
      </p>
      <input data-testid="import-file-input" type="file"
             accept=".pdf,.xls,.xlsx,.csv"
             onChange={(e) => setFile(e.target.files?.[0] || null)}
             style={{ display: "block", marginBottom: 12 }} />
      <textarea data-testid="import-notes-input" placeholder="Note (facoltative)"
                value={note} onChange={(e) => setNote(e.target.value)}
                rows={2}
                style={{ width: "100%", padding: 10, borderRadius: 8,
                         border: "1px solid #d1d5db", fontFamily: "inherit",
                         fontSize: 14, marginBottom: 12 }} />
      <button data-testid="import-analyze-btn"
              disabled={!file || uploading} onClick={submit}
              style={{ padding: "10px 24px", background: "#0F4C81",
                       color: "#fff", borderRadius: 8, border: 0,
                       fontWeight: 600, cursor: uploading ? "wait" : "pointer",
                       opacity: (!file || uploading) ? 0.6 : 1 }}>
        {uploading ? "Analisi in corso…" : "ANALIZZA ORDINE"}
      </button>
      {err && (
        <div data-testid="import-upload-error"
             style={{ marginTop: 12, padding: 10, background: "#fef2f2",
                      border: "1px solid #fecaca", borderRadius: 8,
                      color: "#991b1b", fontSize: 14 }}>
          {err}
          {existingId && (
            <div style={{ marginTop: 6 }}>
              <Link data-testid="open-existing-import"
                    to={`/customer/import-order/${existingId}`}
                    style={{ color: "#0F4C81", fontWeight: 600 }}>
                Apri importazione esistente →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Detail modal / panel ---------- */
function ImportDetailPanel({ id, onClose, onReload }) {
  const { apiGet, apiPost } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState(null);

  const load = useCallback(async () => {
    try { setData(await apiGet(`/api/customer/order-imports/${id}`)); }
    catch (e) { setErr(friendlyError(e)); }
  }, [id, apiGet]);
  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div data-testid="import-detail-error"
           style={{ padding: 24, background: "#fff", borderRadius: 12 }}>
        <p style={{ color: "#991b1b" }}>{err}</p>
        <button onClick={onClose}>Chiudi</button>
      </div>
    );
  }
  if (!data) return <p>Caricamento…</p>;

  const doConfirm = async () => {
    if (!window.confirm("Confermando verrà creato un ordine GE con i prezzi e le condizioni commerciali attualmente applicate. Procedere?")) return;
    setBusy(true); setConfirmMsg(null);
    try {
      const r = await apiPost(`/api/customer/order-imports/${id}/confirm`, {
        delivery_mode: "DELIVERY",
        requested_delivery_date: data.requested_delivery_date || undefined,
      });
      setConfirmMsg({ ok: true, orderId: r.order_id, orderNumber: r.order_number });
      onReload && onReload();
    } catch (e) {
      const code = e?.data?.detail?.code;
      let msg = friendlyError(e);
      if (code === "REQUESTED_DELIVERY_DATE_BEFORE_ESTIMATED") {
        const est = e?.data?.detail?.estimated_delivery_date;
        msg = `La data richiesta è precedente alla data minima di consegna${est ? ` (${est})` : ""}.`;
      } else if (code === "UNRESOLVED_LINES") {
        msg = "Alcune righe non hanno un prodotto GE associato. Il documento richiede una verifica del nostro ufficio.";
      }
      setConfirmMsg({ ok: false, msg });
    } finally { setBusy(false); }
  };

  const unresolved = (data.unresolved_count || 0) > 0
    || data.status === "REVIEW_REQUIRED";
  const canConfirm = data.can_customer_confirm && !busy && !confirmMsg?.ok;

  return (
    <div data-testid="import-detail-panel"
         style={{ background: "#fff", borderRadius: 12, padding: 24,
                  boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>{data.original_filename}</h2>
          <div style={{ marginTop: 6, fontSize: 13, color: "#555" }}>
            <StatusPill status={data.status} />
            {data.external_order_number && <> · Rif. gestionale: <strong>{data.external_order_number}</strong></>}
            {data.requested_delivery_date && <> · Consegna richiesta: <strong>{data.requested_delivery_date}</strong></>}
          </div>
        </div>
        <div>
          <a data-testid="import-open-file"
             href={`${process.env.REACT_APP_BACKEND_URL}/api/customer/order-imports/${id}/file`}
             target="_blank" rel="noreferrer"
             style={{ marginRight: 12, color: "#0F4C81", fontWeight: 600 }}>
            Apri documento originale
          </a>
          <button data-testid="import-close-detail" onClick={onClose}
                  style={{ padding: "6px 12px", border: "1px solid #d1d5db",
                           borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            ← Elenco
          </button>
        </div>
      </div>

      {!data.profile_verified && (
        <div data-testid="import-profile-gate"
             style={{ padding: 12, background: "#fef3c7",
                      border: "1px solid #fde68a", borderRadius: 8,
                      color: "#92400e", marginBottom: 16, fontSize: 14 }}>
          Prima di inviare un ordine devi verificare i dati della tua azienda.
          {" "}
          <Link to="/customer/account" data-testid="import-verify-cta"
                style={{ color: "#0F4C81", fontWeight: 700 }}>
            VERIFICA DATI →
          </Link>
        </div>
      )}

      {unresolved && (
        <div data-testid="import-review-required"
             style={{ padding: 12, background: "#fef3c7",
                      border: "1px solid #fde68a", borderRadius: 8,
                      color: "#92400e", marginBottom: 16, fontSize: 14 }}>
          <strong>Documento ricevuto — ordine non ancora confermato.</strong>{" "}
          Il nostro ufficio GE deve verificare il documento prima che l'ordine
          venga effettivamente acquisito nel nostro sistema. Riceverai una
          conferma via email quando l'ordine sarà stato acquisito e potrà
          essere lavorato. <em>Attenzione: fino a quel momento l'ordine non
          risulta ancora inviato commercialmente.</em>
        </div>
      )}

      {confirmMsg?.ok && (
        <div data-testid="import-confirm-success"
             style={{ padding: 12, background: "#dcfce7",
                      border: "1px solid #86efac", borderRadius: 8,
                      color: "#065f46", marginBottom: 16, fontSize: 14 }}>
          Ordine creato con successo: <strong>{confirmMsg.orderNumber}</strong>{" "}
          <button data-testid="import-open-order"
                  onClick={() => nav(`/customer/orders/${confirmMsg.orderId}`)}
                  style={{ marginLeft: 8, padding: "4px 10px", background: "#0F4C81",
                           color: "#fff", border: 0, borderRadius: 6,
                           cursor: "pointer", fontWeight: 600 }}>
            APRI ORDINE
          </button>
        </div>
      )}
      {confirmMsg?.ok === false && (
        <div data-testid="import-confirm-error"
             style={{ padding: 12, background: "#fef2f2",
                      border: "1px solid #fecaca", borderRadius: 8,
                      color: "#991b1b", marginBottom: 16, fontSize: 14 }}>
          {confirmMsg.msg}
        </div>
      )}

      <ProductTable lines={data.lines || []} preview={data.pricing_preview || {}} />

      <div style={{ marginTop: 8, padding: 10, background: "#f9fafb",
                    borderRadius: 8, fontSize: 12, color: "#666" }}>
        Il prezzo applicato è quello previsto dalle condizioni commerciali GE.
      </div>

      {data.status === "READY" && data.profile_verified && !confirmMsg?.ok && (
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <button data-testid="import-confirm-btn"
                  disabled={!canConfirm} onClick={doConfirm}
                  style={{ padding: "12px 28px", background: "#0F4C81",
                           color: "#fff", borderRadius: 8, border: 0,
                           fontWeight: 700, cursor: canConfirm ? "pointer" : "not-allowed",
                           opacity: canConfirm ? 1 : 0.6, fontSize: 16 }}>
            {busy ? "Creazione ordine…" : "CONFERMA ORDINE"}
          </button>
        </div>
      )}

      {data.created_order_id && (
        <div style={{ marginTop: 16, textAlign: "right" }}>
          <Link data-testid="import-linked-order"
                to={`/customer/orders/${data.created_order_id}`}
                style={{ color: "#0F4C81", fontWeight: 600 }}>
            Apri ordine collegato →
          </Link>
        </div>
      )}
    </div>
  );
}

function ProductTable({ lines, preview }) {
  const priceByLine = {};
  (preview.lines || []).forEach(p => { priceByLine[p.import_line_id] = p; });
  return (
    <div style={{ overflowX: "auto" }}>
      <table data-testid="import-product-table"
             style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ background: "#f6f8fb", textAlign: "left" }}>
            {["Codice", "Prodotto", "Colli", "Kg", "Prezzo GE", "Totale"].map(h =>
              <th key={h} style={{ padding: "8px 10px", fontSize: 12, color: "#555" }}>{h}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {lines.map(l => {
            const gp = priceByLine[l.id];
            const kg = l.normalized_kg;
            const gePrice = gp?.ge_canonical_unit_price;
            const total = (gePrice && kg) ? gePrice * kg : null;
            const matched = l.match_status === "MATCHED";
            return (
              <tr key={l.id} data-testid={`import-line-${l.row_number}`}
                  style={{ borderTop: "1px solid #eef1f5",
                           background: matched ? "#fff" : "#fffbeb" }}>
                <td style={{ padding: "8px 10px" }}>
                  <code>{l.matched_product?.sku || l.external_sku}</code>
                </td>
                <td style={{ padding: "8px 10px" }}>
                  {l.matched_product?.name || l.external_description || "—"}
                  {!matched && <div style={{ fontSize: 11, color: "#92400e" }}>Verifica GE necessaria</div>}
                </td>
                <td style={{ padding: "8px 10px" }}>{l.normalized_cartons ?? "—"}</td>
                <td style={{ padding: "8px 10px" }}>{kg != null ? kg.toLocaleString("it-IT") : "—"}</td>
                <td style={{ padding: "8px 10px" }}>{eur(gePrice)}</td>
                <td style={{ padding: "8px 10px", fontWeight: 600 }}>{eur(total)}</td>
              </tr>
            );
          })}
        </tbody>
        {preview.total != null && (
          <tfoot>
            <tr>
              <td colSpan={5} style={{ padding: "10px", textAlign: "right",
                                        fontWeight: 700 }}>Totale GE</td>
              <td style={{ padding: "10px", fontWeight: 700, fontSize: 16 }}>
                {eur(preview.total)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/* ---------- Main page: list + upload + detail ---------- */
export default function CustomerImportOrderPage() {
  const { apiGet } = useAuth();
  const [items, setItems] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [pilotBlocked, setPilotBlocked] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await apiGet("/api/customer/order-imports?limit=30");
      setItems(r.items || []);
    } catch (e) {
      if (e?.data?.detail?.code === "PILOT_ONLY") setPilotBlocked(true);
      else setItems([]);
    }
  }, [apiGet]);
  useEffect(() => { reload(); }, [reload]);

  if (selectedId) {
    return (
      <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}
           data-testid="customer-import-order-page">
        <ImportDetailPanel id={selectedId}
                            onClose={() => { setSelectedId(null); reload(); }}
                            onReload={reload} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}
         data-testid="customer-import-order-page">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, margin: 0 }}>Importa ordine</h1>
        <p style={{ color: "#666", marginTop: 6 }}>
          Carica il file generato dal tuo gestionale. Controlleremo automaticamente
          articoli, quantità e condizioni commerciali GE.
        </p>
      </div>

      {pilotBlocked && (
        <div data-testid="import-pilot-blocked"
             style={{ padding: 14, background: "#fef3c7", border: "1px solid #fde68a",
                      borderRadius: 8, color: "#92400e", marginBottom: 16 }}>
          Questa funzione è disponibile solo per i Customer autorizzati.
          Contatta GE per attivarla.
        </div>
      )}

      {!pilotBlocked && <UploadCard onUploaded={(id) => { setSelectedId(id); reload(); }} />}

      <div style={{ background: "#fff", borderRadius: 12, padding: 20,
                    boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
        <h2 style={{ fontSize: 20, margin: "0 0 12px" }}>I miei ordini importati</h2>
        {items === null && <p>Caricamento…</p>}
        {items && items.length === 0 && (
          <p style={{ color: "#888" }}>Non hai ancora importato ordini.</p>
        )}
        {items && items.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table data-testid="import-list-table"
                   style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#f6f8fb", textAlign: "left" }}>
                  {["Data", "File", "Rif. gestionale", "Consegna", "Stato", "Ordine GE"].map(h =>
                    <th key={h} style={{ padding: "8px 10px", fontSize: 12, color: "#555" }}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} data-testid={`import-row-${it.id}`}
                      style={{ borderTop: "1px solid #eef1f5", cursor: "pointer" }}
                      onClick={() => setSelectedId(it.id)}>
                    <td style={{ padding: "8px 10px" }}>{it.created_at ? new Date(it.created_at).toLocaleDateString("it-IT") : "—"}</td>
                    <td style={{ padding: "8px 10px" }}>{it.original_filename}</td>
                    <td style={{ padding: "8px 10px" }}>{it.external_order_number || "—"}</td>
                    <td style={{ padding: "8px 10px" }}>{it.requested_delivery_date || "—"}</td>
                    <td style={{ padding: "8px 10px" }}><StatusPill status={it.status} /></td>
                    <td style={{ padding: "8px 10px" }}>
                      {it.created_order_id
                        ? <Link to={`/customer/orders/${it.created_order_id}`}
                                 data-testid={`import-order-link-${it.id}`}
                                 onClick={(e) => e.stopPropagation()}
                                 style={{ color: "#0F4C81", fontWeight: 600 }}>
                            Apri
                          </Link>
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
